/**
 * idb/idb-client.js
 * ---------------------------------------------------------------------------
 * The main-thread IndexedDB client. Delegates straight to `idb-core` (no Web
 * Worker, no postMessage) while preserving the exact store-passing API the old
 * worker client exposed, so `useIndexedDBStore` and `useMessageStore` — and
 * therefore `useMessageHistory` + `reconcileMessages` — are unchanged.
 *
 * The worker was removed (it gave negligible gain and required Vite `?worker&url`
 * + a cross-origin blob/importScripts bootstrap). `idb-core` already contained the
 * whole IndexedDB layer as plain async functions using the global `indexedDB`, so
 * "main-thread" is simply calling those functions directly. Every method returns a
 * Promise, exactly as before.
 */
import * as core from './idb-core';

/**
 * Public API. Generic CRUD methods take a `store` name so one client serves every
 * object store; the cursor ops hardcode the messages store inside `idb-core`.
 */
export const idbClient = {
  // Generic CRUD -----------------------------------------------------------
  getAll: (store) => core.getAll(store),
  add: (store, record) => core.add(store, record),
  update: (store, record) => core.update(store, record),
  get: (store, key) => core.get(store, key),
  put: (store, record) => core.put(store, record),

  // Cursor-driven message ops (store implied by the op) --------------------
  insertIfAbsent: (store, record, matchField) => core.insertIfAbsent(store, record, matchField),
  clearMessages: (opts) => core.clearMessages(opts || {}),
  cleanOrphans: (opts) => core.cleanOrphans(opts || {}),
};
